import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { 
  Play, Pause, Users, Volume2, VolumeX, RotateCcw, Crown, 
  Copy, Check, Settings, Loader2, Music, Square, Trophy, TrendingUp, Hash, Ticket, Printer, Monitor, AlertTriangle, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

import { useSearchParams } from 'react-router-dom';

export default function BingoHostPage() {
  const { gameCode: urlGameCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [hostName, setHostName] = useState('');
  const [winPattern, setWinPattern] = useState('line');
  const [autoCallInterval, setAutoCallInterval] = useState(15);
  const [clubId, setClubId] = useState(searchParams.get('club') || '');
  const [clubs, setClubs] = useState([]);
  const [userClubs, setUserClubs] = useState([]); // Clubs owned by the current user
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [calling, setCalling] = useState(false);
  const [autoCallActive, setAutoCallActive] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lastCalledDance, setLastCalledDance] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false); // Default OFF
  
  // Paper ticket states
  const [paperTickets, setPaperTickets] = useState([]);
  const [showPaperTicketDialog, setShowPaperTicketDialog] = useState(false);
  const [paperTicketCount, setPaperTicketCount] = useState(25);
  const [generatingTickets, setGeneratingTickets] = useState(false);
  const [verifyTicketCode, setVerifyTicketCode] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [paperWinnerAlert, setPaperWinnerAlert] = useState(null);
  
  // Exclude tickets states
  const [showExcludeDialog, setShowExcludeDialog] = useState(false);
  const [excludedTickets, setExcludedTickets] = useState(new Set());
  const [excludeFromTicket, setExcludeFromTicket] = useState('');

  const joinUrl = game ? `${window.location.origin}/bingo/play/${game.game_code}` : '';
  const displayUrl = game ? `${window.location.origin}/bingo/display/${game.game_code}` : '';

  // Fetch user's owned clubs (for club-specific hosting)
  useEffect(() => {
    const fetchUserClubs = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const userData = await res.json();
          // Fetch clubs owned by this user that have bingo enabled
          const clubsRes = await fetch(`${API_URL}/api/clubs`);
          if (clubsRes.ok) {
            const allClubs = await clubsRes.json();
            const ownedClubs = allClubs.filter(c => 
              c.owner_id === userData.user_id && c.bingo_enabled
            );
            setUserClubs(ownedClubs);
            
            // If user owns clubs with bingo enabled and no club pre-selected, auto-select the first one
            if (ownedClubs.length > 0 && !searchParams.get('club')) {
              setClubId(ownedClubs[0].club_id);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching user clubs:', error);
      }
    };
    fetchUserClubs();
  }, [searchParams]);

  // Fetch all clubs for dropdown (admin mode or when user has no clubs)
  useEffect(() => {
    fetch(`${API_URL}/api/clubs`)
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        // Only show clubs with bingo enabled
        const bingoEnabledClubs = data.filter(c => c.bingo_enabled);
        setClubs(bingoEnabledClubs);
      })
      .catch(() => {});
  }, []);

  // Fetch game state
  const fetchGame = useCallback(async () => {
    if (!game?.game_code) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}`);
      if (res.ok) {
        const data = await res.json();
        setGame(data);
      }
    } catch (error) {
      console.error('Error fetching game:', error);
    }
  }, [game?.game_code]);

  // Poll for updates
  useEffect(() => {
    if (!game?.game_code) return;
    
    const interval = setInterval(fetchGame, 2000);
    return () => clearInterval(interval);
  }, [game?.game_code, fetchGame]);

  // Load existing game from URL
  useEffect(() => {
    if (urlGameCode) {
      setLoading(true);
      fetch(`${API_URL}/api/bingo/games/${urlGameCode}`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) setGame(data);
          else toast.error('Game not found');
        })
        .catch(() => toast.error('Failed to load game'))
        .finally(() => setLoading(false));
    }
  }, [urlGameCode]);

  // Auto-caller
  useEffect(() => {
    if (!autoCallActive || !game || game.status !== 'active') return;
    
    const interval = setInterval(async () => {
      await callDance();
    }, autoCallInterval * 1000);
    
    return () => clearInterval(interval);
  }, [autoCallActive, game?.status, autoCallInterval]);

  const createGame = async () => {
    if (!hostName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/bingo/games`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          host_name: hostName,
          win_pattern: winPattern,
          auto_call: false,
          auto_call_interval: autoCallInterval,
          club_id: clubId || null
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setGame(data);
        navigate(`/bingo/host/${data.game_code}`, { replace: true });
        toast.success('Game created!');
      } else {
        toast.error('Failed to create game');
      }
    } catch (error) {
      toast.error('Failed to create game');
    } finally {
      setCreating(false);
    }
  };

  const startGame = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/start`, {
        method: 'POST'
      });
      
      if (res.ok) {
        fetchGame();
        toast.success('Game started!');
      }
    } catch (error) {
      toast.error('Failed to start game');
    }
  };

  const callDance = async () => {
    if (calling) return;
    
    setCalling(true);
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (res.ok) {
        const data = await res.json();
        setLastCalledDance(data.called_dance);
        fetchGame();
        
        // Check if paper ticket winner detected - show alert
        if (data.paper_ticket_winners && data.paper_ticket_winners.length > 0) {
          const winnerCodes = data.paper_ticket_winners.map(w => w.ticket_code).join(', ');
          setPaperWinnerAlert(data.paper_ticket_winners);
          toast.warning(`🎫 POSSIBLE WINNER! Paper ticket(s): ${winnerCodes}`, {
            duration: 10000,
          });
          setAutoCallActive(false); // Stop auto-call
        }
        
        // Speak the dance name (only if audio is enabled)
        if (audioEnabled && 'speechSynthesis' in window) {
          const utterance = new SpeechSynthesisUtterance(data.called_dance);
          utterance.rate = 0.9;
          speechSynthesis.speak(utterance);
        }
      } else {
        const error = await res.json();
        if (error.detail === 'All dances have been called') {
          toast.info('All dances have been called!');
          setAutoCallActive(false);
        } else if (error.detail === 'Game is not active') {
          toast.info('Game is paused - resume to continue');
          setAutoCallActive(false);
        }
      }
    } catch (error) {
      console.error('Error calling dance:', error);
    } finally {
      setCalling(false);
    }
  };

  const resetGame = async () => {
    if (!window.confirm('Reset game for a new round? All players will get new cards.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/reset`, {
        method: 'POST'
      });
      
      if (res.ok) {
        setAutoCallActive(false);
        setLastCalledDance(null);
        fetchGame();
        toast.success('Game reset for new round!');
      }
    } catch (error) {
      toast.error('Failed to reset game');
    }
  };

  const endGame = async () => {
    if (!window.confirm('End this game?')) return;
    
    try {
      await fetch(`${API_URL}/api/bingo/games/${game.game_code}/end`, {
        method: 'POST'
      });
      setAutoCallActive(false);
      fetchGame();
      toast.success('Game ended');
    } catch (error) {
      toast.error('Failed to end game');
    }
  };

  const resumeGame = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/resume`, {
        method: 'POST'
      });
      if (res.ok) {
        fetchGame();
        setPaperWinnerAlert(null);
        toast.success('Game resumed!');
      }
    } catch (error) {
      toast.error('Failed to resume game');
    }
  };

  // Paper Ticket Functions
  const generatePaperTickets = async () => {
    if (!game?.game_code) return;
    
    setGeneratingTickets(true);
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/paper-tickets/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: paperTicketCount })
      });
      
      if (res.ok) {
        const data = await res.json();
        setPaperTickets(data.tickets);
        toast.success(`Generated ${data.tickets.length} paper tickets!`);
        setShowPaperTicketDialog(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to generate tickets');
      }
    } catch (error) {
      toast.error('Error generating paper tickets');
    } finally {
      setGeneratingTickets(false);
    }
  };

  const fetchPaperTickets = async () => {
    if (!game?.game_code) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/paper-tickets`);
      if (res.ok) {
        const data = await res.json();
        setPaperTickets(data.tickets || []);
        // Update excluded tickets from server
        if (data.excluded_tickets) {
          setExcludedTickets(new Set(data.excluded_tickets));
        }
      }
    } catch (error) {
      console.error('Error fetching paper tickets:', error);
    }
  };

  const downloadPaperTicketsPDF = () => {
    if (!game?.game_code) return;
    const url = `${API_URL}/api/bingo/games/${game.game_code}/paper-tickets/pdf?count=${paperTickets.length}`;
    window.open(url, '_blank');
  };

  const verifyPaperTicket = async () => {
    if (!verifyTicketCode.trim() || !game?.game_code) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/paper-tickets/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_code: verifyTicketCode.toUpperCase() })
      });
      
      const data = await res.json();
      setVerifyResult(data);
      
      if (data.is_winner) {
        toast.success(`🎉 WINNER! Ticket ${verifyTicketCode.toUpperCase()} has a winning line!`);
      } else if (data.valid) {
        toast.info(`Ticket valid but not a winner yet (${data.matches_count}/5 in best column)`);
      } else {
        toast.error(data.message || 'Invalid ticket code');
      }
    } catch (error) {
      toast.error('Error verifying ticket');
    }
  };

  // Save excluded tickets to server
  const handleSaveExcludedTickets = async () => {
    if (!game?.game_code) return;
    
    try {
      const res = await fetch(`${API_URL}/api/bingo/games/${game.game_code}/paper-tickets/exclude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ excluded_tickets: Array.from(excludedTickets) })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Excluded tickets updated');
        setShowExcludeDialog(false);
        fetchPaperTickets();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to update excluded tickets');
      }
    } catch (error) {
      toast.error('Error updating excluded tickets');
    }
  };

  // Toggle a ticket's excluded status
  const toggleExcludeTicket = (ticketNum) => {
    setExcludedTickets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ticketNum)) {
        newSet.delete(ticketNum);
      } else {
        newSet.add(ticketNum);
      }
      return newSet;
    });
  };

  // Exclude all tickets from a certain number onwards
  const excludeFromNumber = () => {
    const fromNum = parseInt(excludeFromTicket);
    if (isNaN(fromNum) || fromNum < 1) {
      toast.error('Please enter a valid ticket number');
      return;
    }
    const maxTicket = paperTickets.length;
    if (fromNum > maxTicket) {
      toast.error(`Ticket number must be ${maxTicket} or less`);
      return;
    }
    
    const newExcluded = new Set(excludedTickets);
    for (let i = fromNum; i <= maxTicket; i++) {
      newExcluded.add(i);
    }
    setExcludedTickets(newExcluded);
    toast.success(`Excluded tickets ${fromNum} to ${maxTicket}`);
    setExcludeFromTicket('');
  };

  // Clear all exclusions
  const clearExclusions = () => {
    setExcludedTickets(new Set());
  };

  // Fetch paper tickets when game loads
  useEffect(() => {
    if (game?.game_code) {
      fetchPaperTickets();
    }
  }, [game?.game_code]);

  const copyJoinLink = () => {
    navigator.clipboard.writeText(joinUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const updateSettings = async (newPattern) => {
    try {
      await fetch(`${API_URL}/api/bingo/games/${game.game_code}/settings?win_pattern=${newPattern}`, {
        method: 'POST'
      });
      fetchGame();
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  // Create game form
  if (!game) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-900/30 to-slate-950 py-12 px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-violet-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Music className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Line Dance Bingo</h1>
            <p className="text-slate-400">Create a game and invite players</p>
          </div>
          
          {/* Game Type Switcher */}
          <div className="flex flex-wrap justify-center gap-2 mb-6">
            <div className="bg-violet-600 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
              <Music className="w-4 h-4" />
              Line Dance
            </div>
            <Link to="/bingo/uk90/host" className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors">
              <Hash className="w-4 h-4" />
              UK 90-Ball
            </Link>
            <Link to="/raffle/host" className="bg-slate-800 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors">
              <Ticket className="w-4 h-4" />
              Raffle
            </Link>
          </div>
          
          <Card className="bg-slate-900 border-slate-800">
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Your Name (Host)</label>
                <Input
                  value={hostName}
                  onChange={(e) => setHostName(e.target.value)}
                  placeholder="Enter your name"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Win Pattern</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setWinPattern('line')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      winPattern === 'line' 
                        ? 'border-violet-500 bg-violet-900/30' 
                        : 'border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="text-white font-medium">Line</div>
                    <div className="text-xs text-slate-400">5 down wins</div>
                  </button>
                  <button
                    onClick={() => setWinPattern('fullhouse')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      winPattern === 'fullhouse' 
                        ? 'border-violet-500 bg-violet-900/30' 
                        : 'border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="text-white font-medium">Full House</div>
                    <div className="text-xs text-slate-400">All squares</div>
                  </button>
                  <button
                    onClick={() => setWinPattern('ognib')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      winPattern === 'ognib' 
                        ? 'border-red-500 bg-red-900/30' 
                        : 'border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="text-white font-medium">OGNIB</div>
                    <div className="text-xs text-slate-400">Reverse! Last standing wins</div>
                  </button>
                </div>
              </div>
              
              <div>
                <label className="text-sm text-slate-400 mb-1 block">Auto-Call Speed (seconds)</label>
                <Input
                  type="number"
                  min="5"
                  max="60"
                  value={autoCallInterval}
                  onChange={(e) => setAutoCallInterval(parseInt(e.target.value) || 15)}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              {/* Club Selection - Show user's clubs if they have any with bingo enabled */}
              {userClubs.length > 0 ? (
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Your Club</label>
                  {userClubs.length === 1 ? (
                    <div className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white flex items-center">
                      {userClubs[0].name}
                    </div>
                  ) : (
                    <select
                      value={clubId}
                      onChange={(e) => setClubId(e.target.value)}
                      className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
                    >
                      {userClubs.map(club => (
                        <option key={club.club_id} value={club.club_id}>{club.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              ) : clubs.length > 0 && (
                <div>
                  <label className="text-sm text-slate-400 mb-1 block">Link to Club (optional)</label>
                  <select
                    value={clubId}
                    onChange={(e) => setClubId(e.target.value)}
                    className="w-full h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-white"
                  >
                    <option value="">No club - standalone game</option>
                    {clubs.map(club => (
                      <option key={club.club_id} value={club.club_id}>{club.name}</option>
                    ))}
                  </select>
                </div>
              )}
              
              <Button
                onClick={createGame}
                disabled={creating}
                className="w-full bg-violet-600 hover:bg-violet-500 h-12 text-lg"
              >
                {creating ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Play className="w-5 h-5 mr-2" />
                )}
                Create Game
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Game host dashboard
  return (
    <div className="min-h-screen bg-slate-950 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Music className="w-6 h-6 text-violet-400" />
              {game.win_pattern === 'ognib' ? 'OGNIB - Reverse Bingo' : 'Line Dance Bingo'}
            </h1>
            <p className="text-slate-400">
              Host: {game.host_name}
              {game.club_name && <span className="ml-2 text-violet-400">• {game.club_name}</span>}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Audio Toggle */}
            <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-slate-800/50">
              <button
                onClick={() => setAudioEnabled(!audioEnabled)}
                className={`p-1.5 rounded transition-colors ${audioEnabled ? 'text-violet-400' : 'text-slate-400'}`}
                title={audioEnabled ? 'Audio On - Click to mute' : 'Audio Off - Click to enable'}
              >
                {audioEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </button>
            </div>
            <Badge className={`${
              game.status === 'waiting' ? 'bg-yellow-500' :
              game.status === 'active' ? 'bg-green-500' : 'bg-slate-500'
            }`}>
              {game.status === 'waiting' ? 'Waiting for players' :
               game.status === 'active' ? 'Game in progress' : 'Game ended'}
            </Badge>
            <Badge variant="outline" className={`border-slate-600 ${game.win_pattern === 'ognib' ? 'text-red-400 border-red-600' : 'text-white'}`}>
              {game.win_pattern === 'line' ? 'Line wins (5 down)' : 
               game.win_pattern === 'ognib' ? 'OGNIB - Last standing wins!' : 'Full house (all 15)'}
            </Badge>
          </div>
        </div>

        {/* Main Layout - Controls Left, Leaderboard Right */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left Side - QR Code & Game Controls */}
          <div className="lg:w-2/3 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* QR Code & Join Info */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg">Join Game</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-white p-4 rounded-lg w-fit mx-auto">
                    <QRCodeSVG value={joinUrl} size={150} />
                  </div>
                  
                  <div className="text-center">
                    <div className="text-3xl font-mono font-bold text-violet-400 tracking-wider">
                      {game.game_code}
                    </div>
                    <p className="text-sm text-slate-400 mt-1">Game Code</p>
                  </div>
                  
                  <Button
                    variant="outline"
                    onClick={copyJoinLink}
                    className="w-full border-slate-700 text-white"
                  >
                    {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {copied ? 'Copied!' : 'Copy Join Link'}
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => window.open(displayUrl, '_blank')}
                    className="w-full border-emerald-700 text-emerald-400 hover:bg-emerald-900/20"
                  >
                    <Monitor className="w-4 h-4 mr-2" />
                    Open TV Display
                  </Button>
                  
                  <div className="flex items-center gap-2 text-slate-400">
                    <Users className="w-4 h-4" />
                    <span>{game.players?.length || 0} players joined</span>
                  </div>
                  
                  {game.players?.length > 0 && (
                    <div className="text-sm text-slate-400 max-h-20 overflow-y-auto">
                      {game.players.map(p => p.nickname).join(', ')}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Game Controls */}
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg">Game Controls</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Winner Banner */}
                  {game.winners?.length > 0 && (
                    <div className={`rounded-lg p-4 text-center animate-pulse ${
                      game.win_pattern === 'ognib' 
                        ? 'bg-gradient-to-r from-red-600 to-orange-500' 
                        : 'bg-gradient-to-r from-yellow-600 to-amber-500'
                    }`}>
                      <Crown className="w-8 h-8 mx-auto text-white mb-2" />
                      <div className="text-xl font-bold text-white">
                        {game.winners[0].nickname} WINS!
                      </div>
                      {game.win_pattern === 'ognib' && (
                        <div className="text-sm text-white/80 mt-1">Last one standing!</div>
                      )}
                    </div>
                  )}

                  {/* Current Dance */}
                  {lastCalledDance && game.status === 'active' && (
                    <div className={`rounded-lg p-4 text-center ${
                      game.win_pattern === 'ognib' ? 'bg-red-900/50' : 'bg-violet-900/50'
                    }`}>
                      <div className={`text-sm mb-1 ${game.win_pattern === 'ognib' ? 'text-red-300' : 'text-violet-300'}`}>
                        {game.win_pattern === 'ognib' ? 'Danger Dance!' : 'Current Dance'}
                      </div>
                      <div className="text-2xl font-bold text-white">{lastCalledDance}</div>
                    </div>
                  )}

                  {/* Control Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    {game.status === 'waiting' && (
                      <Button
                        onClick={startGame}
                        disabled={!game.players?.length && !paperTickets.length}
                        className="col-span-2 bg-green-600 hover:bg-green-500 h-12"
                      >
                        <Play className="w-5 h-5 mr-2" />
                        Start Game ({game.players?.length || 0} online{paperTickets.length > 0 ? ` + ${paperTickets.length} paper` : ''})
                      </Button>
                    )}
                    
                    {/* Paused State - Paper Winner Alert */}
                    {game.status === 'paused' && (
                      <div className="col-span-2 space-y-2">
                        <div className="bg-yellow-600/30 border-2 border-yellow-500 rounded-lg p-4 text-center animate-pulse">
                          <AlertTriangle className="w-8 h-8 mx-auto text-yellow-400 mb-2" />
                          <div className="text-lg font-bold text-yellow-300">GAME PAUSED</div>
                          <div className="text-sm text-yellow-200">Paper ticket may have won!</div>
                          <div className="text-xs text-yellow-300/70 mt-1">Verify the claim, then resume or end the game</div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            onClick={resumeGame}
                            className="bg-green-600 hover:bg-green-500 h-10"
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Resume Game
                          </Button>
                          <Button
                            onClick={endGame}
                            className="bg-red-600 hover:bg-red-500 h-10"
                          >
                            <Square className="w-4 h-4 mr-1" />
                            End (Winner!)
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {game.status === 'active' && (
                      <>
                        <Button
                          onClick={callDance}
                          disabled={calling}
                          className={`h-12 ${game.win_pattern === 'ognib' ? 'bg-red-600 hover:bg-red-500' : 'bg-violet-600 hover:bg-violet-500'}`}
                        >
                          {calling ? (
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                          ) : (
                            <Volume2 className="w-5 h-5 mr-2" />
                          )}
                          Call Dance
                        </Button>
                        
                        <Button
                          onClick={() => setAutoCallActive(!autoCallActive)}
                          className={`h-12 ${
                            autoCallActive 
                              ? 'bg-slate-600 hover:bg-slate-500' 
                              : 'bg-blue-600 hover:bg-blue-500'
                          }`}
                        >
                          {autoCallActive ? (
                            <><Pause className="w-4 h-4 mr-1" /> Stop</>
                          ) : (
                            <><Play className="w-4 h-4 mr-1" /> Auto</>
                          )}
                        </Button>
                      </>
                    )}
                    
                    <Button
                      onClick={resetGame}
                      variant="outline"
                      className="border-slate-700 text-white hover:bg-slate-800"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                    
                    <Button
                      onClick={endGame}
                      variant="outline"
                      className="border-red-700 text-red-400 hover:bg-red-900/20"
                    >
                      <Square className="w-4 h-4 mr-1" />
                      End
                    </Button>
                  </div>

                  {/* Settings */}
                  {game.status === 'waiting' && (
                    <div className="pt-3 border-t border-slate-800">
                      <div className="flex items-center gap-2 text-slate-400 mb-2">
                        <Settings className="w-4 h-4" />
                        <span className="text-sm">Win Pattern</span>
                      </div>
                      <div className="grid grid-cols-3 gap-1">
                        <button
                          onClick={() => updateSettings('line')}
                          className={`p-2 rounded border text-xs ${
                            game.win_pattern === 'line'
                              ? 'border-violet-500 bg-violet-900/30 text-white'
                              : 'border-slate-700 text-slate-400'
                          }`}
                        >
                          Line
                        </button>
                        <button
                          onClick={() => updateSettings('fullhouse')}
                          className={`p-2 rounded border text-xs ${
                            game.win_pattern === 'fullhouse'
                              ? 'border-violet-500 bg-violet-900/30 text-white'
                              : 'border-slate-700 text-slate-400'
                          }`}
                        >
                          Full House
                        </button>
                        <button
                          onClick={() => updateSettings('ognib')}
                          className={`p-2 rounded border text-xs ${
                            game.win_pattern === 'ognib'
                              ? 'border-red-500 bg-red-900/30 text-white'
                              : 'border-slate-700 text-slate-400'
                          }`}
                        >
                          OGNIB
                        </button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Called Dances - Full Width Below */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg">
                  Called Dances ({game.called_dances?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
                  {game.called_dances?.map((dance, idx) => (
                    <Badge 
                      key={idx} 
                      variant="secondary"
                      className={`text-xs ${
                        idx === game.called_dances.length - 1 
                          ? (game.win_pattern === 'ognib' ? 'bg-red-600 text-white' : 'bg-violet-600 text-white')
                          : 'bg-slate-800 text-slate-300'
                      }`}
                    >
                      {dance}
                    </Badge>
                  ))}
                  {(!game.called_dances || game.called_dances.length === 0) && (
                    <span className="text-slate-400 text-sm">No dances called yet</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Side - Leaderboard (Sticky) */}
          <div className="lg:w-1/3">
            <div className="lg:sticky lg:top-4">
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-violet-400" />
                    {game.win_pattern === 'ognib' ? 'Survivors' : 'Live Leaderboard'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {game.leaderboard && game.leaderboard.length > 0 ? (
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                      {game.leaderboard.map((player, idx) => (
                        <div 
                          key={player.player_id}
                          className={`flex items-center justify-between p-2 rounded-lg ${
                            player.eliminated 
                              ? 'bg-red-900/20 border border-red-800/50 opacity-60' 
                              : player.is_paper_ticket
                                ? 'bg-emerald-900/30 border border-emerald-600/50'
                                : idx === 0 
                                  ? 'bg-violet-900/30 border border-violet-500/50' 
                                  : 'bg-slate-800/50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                              player.eliminated ? 'bg-red-800 text-red-200' :
                              player.is_paper_ticket ? 'bg-emerald-600 text-white' :
                              idx === 0 ? 'bg-yellow-500 text-black' :
                              idx === 1 ? 'bg-slate-400 text-black' :
                              idx === 2 ? 'bg-amber-700 text-white' :
                              'bg-slate-700 text-white'
                            }`}>
                              {player.eliminated ? 'X' : player.is_paper_ticket ? '🎫' : idx + 1}
                            </div>
                            <span className={`font-medium ${
                              player.eliminated ? 'text-red-300 line-through' : 
                              player.is_paper_ticket ? 'text-emerald-300 font-mono text-sm' :
                              'text-white'
                            }`}>
                              {player.is_paper_ticket ? player.nickname.replace('🎫 ', '') : player.nickname}
                            </span>
                            {player.is_paper_ticket && (
                              <span className="text-xs text-emerald-500 hidden sm:inline">(paper)</span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {game.win_pattern === 'ognib' ? (
                              /* OGNIB: Show hits count */
                              <div className={`text-right ${player.eliminated ? 'text-red-400' : 'text-white'}`}>
                                <div className="text-sm font-bold">
                                  {player.hits_count || 0} hit{(player.hits_count || 0) !== 1 ? 's' : ''}
                                </div>
                                {player.eliminated && (
                                  <div className="text-xs text-red-500">OUT</div>
                                )}
                              </div>
                            ) : (
                              /* Regular bingo: Show column progress (5 down wins) */
                              <>
                                <div className="hidden sm:flex items-center gap-1">
                                  <span className="text-xs text-slate-400">Col:</span>
                                  <div className="w-12 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full transition-all duration-300 ${
                                        player.best_col_progress === 5 ? 'bg-green-500' :
                                        player.best_col_progress >= 4 ? 'bg-yellow-500' :
                                        'bg-violet-500'
                                      }`}
                                      style={{ width: `${(player.best_col_progress / 5) * 100}%` }}
                                    />
                                  </div>
                                  <span className="text-xs font-mono text-violet-400 w-6">
                                    {player.best_col_progress}/5
                                  </span>
                                </div>
                                <div className="text-right">
                                  <div className="text-sm font-bold text-white">
                                    {player.marked_count}
                                  </div>
                                  <div className="text-xs text-slate-400">/15</div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Waiting for players...</p>
                    </div>
                  )}
                  
                  {/* OGNIB Stats */}
                  {game.win_pattern === 'ognib' && game.leaderboard && game.status === 'active' && (
                    <div className="mt-4 pt-3 border-t border-slate-800">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Still in game:</span>
                        <span className="text-green-400 font-bold">
                          {game.leaderboard.filter(p => !p.eliminated).length}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Eliminated:</span>
                        <span className="text-red-400 font-bold">
                          {game.leaderboard.filter(p => p.eliminated).length}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Paper Tickets Section */}
              <Card className="bg-slate-900 border-slate-800 mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Printer className="w-5 h-5 text-emerald-400" />
                    Paper Tickets
                    {paperTickets.length > 0 && (
                      <Badge variant="secondary" className="ml-2 bg-emerald-600">
                        {paperTickets.length}
                      </Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-slate-400">
                    For players without phones - same game, paper cards
                  </p>
                  
                  {/* Generate/Download/Exclude buttons */}
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      onClick={() => setShowPaperTicketDialog(true)}
                      size="sm"
                      variant="outline"
                      className="border-slate-700 text-white hover:bg-slate-800"
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      {paperTickets.length > 0 ? 'More' : 'Generate'}
                    </Button>
                    {paperTickets.length > 0 && (
                      <>
                        <Button
                          onClick={downloadPaperTicketsPDF}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                        {game.status !== 'active' && (
                          <Button
                            onClick={() => setShowExcludeDialog(true)}
                            size="sm"
                            variant="outline"
                            className="border-orange-600 text-orange-400 hover:bg-orange-900/20"
                          >
                            <Settings className="w-4 h-4 mr-1" />
                            Exclude
                            {excludedTickets.size > 0 && (
                              <span className="ml-1 bg-orange-500 text-white text-xs px-1.5 rounded-full">{excludedTickets.size}</span>
                            )}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Show excluded count during active game */}
                  {game.status === 'active' && excludedTickets.size > 0 && (
                    <p className="text-xs text-orange-400">
                      {excludedTickets.size} ticket{excludedTickets.size > 1 ? 's' : ''} excluded (unsold)
                    </p>
                  )}
                  
                  {/* Verify Paper Ticket */}
                  {game.status === 'active' && (
                    <div className="pt-2 border-t border-slate-800">
                      <label className="text-xs text-slate-400 block mb-1">Verify Paper Ticket Claim</label>
                      <div className="flex gap-2">
                        <Input
                          value={verifyTicketCode}
                          onChange={(e) => setVerifyTicketCode(e.target.value.toUpperCase())}
                          placeholder="Enter code"
                          className="bg-slate-800 border-slate-700 text-white text-sm h-9 font-mono"
                          maxLength={6}
                        />
                        <Button
                          onClick={verifyPaperTicket}
                          size="sm"
                          disabled={!verifyTicketCode.trim()}
                          className="bg-violet-600 hover:bg-violet-700"
                        >
                          Check
                        </Button>
                      </div>
                      
                      {/* Verify Result */}
                      {verifyResult && (
                        <div className={`mt-2 p-2 rounded text-sm ${
                          verifyResult.is_winner 
                            ? 'bg-green-900/50 border border-green-600 text-green-300' 
                            : verifyResult.valid
                              ? 'bg-yellow-900/50 border border-yellow-600 text-yellow-300'
                              : 'bg-red-900/50 border border-red-600 text-red-300'
                        }`}>
                          {verifyResult.is_winner ? (
                            <div className="flex items-center gap-2">
                              <Crown className="w-4 h-4" />
                              WINNER! Column complete
                            </div>
                          ) : verifyResult.valid ? (
                            <div>
                              Not yet - {verifyResult.matches_count}/5 in best column
                            </div>
                          ) : (
                            <div>Invalid ticket code</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        
        {/* Paper Ticket Generate Dialog */}
        {showPaperTicketDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="bg-slate-900 border-slate-700 w-full max-w-sm mx-4">
              <CardHeader>
                <CardTitle className="text-white">Generate Paper Tickets</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-1">Number of tickets</label>
                  <Input
                    type="number"
                    value={paperTicketCount}
                    onChange={(e) => setPaperTicketCount(Math.max(1, Math.min(200, parseInt(e.target.value) || 1)))}
                    min={1}
                    max={200}
                    className="bg-slate-800 border-slate-700 text-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Each ticket has a unique 5×5 grid of dances
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowPaperTicketDialog(false)}
                    variant="outline"
                    className="flex-1 border-slate-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={generatePaperTickets}
                    disabled={generatingTickets}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {generatingTickets ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating...</>
                    ) : (
                      'Generate'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Exclude Unsold Tickets Dialog */}
        <Dialog open={showExcludeDialog} onOpenChange={setShowExcludeDialog}>
          <DialogContent className="max-w-lg bg-slate-900 border-slate-800 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white">
                <Settings className="w-5 h-5 text-orange-500" />
                Exclude Unsold Tickets
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-slate-400">
                Mark tickets that weren't sold so they won't count as winners.
                You have <strong className="text-white">{paperTickets.length}</strong> tickets.
              </p>
              
              {/* Quick exclude from number */}
              <div className="bg-orange-900/30 p-4 rounded-lg space-y-2 border border-orange-800">
                <label className="block text-sm font-medium text-orange-300">Quick: Exclude from ticket # onwards</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    value={excludeFromTicket}
                    onChange={(e) => setExcludeFromTicket(e.target.value)}
                    placeholder="e.g., 39"
                    min={1}
                    max={paperTickets.length}
                    className="w-24 bg-slate-800 border-slate-700 text-white"
                  />
                  <Button 
                    onClick={excludeFromNumber} 
                    variant="outline" 
                    size="sm"
                    className="border-orange-600 text-orange-400 hover:bg-orange-900/30"
                  >
                    Exclude {excludeFromTicket && `#${excludeFromTicket}-${paperTickets.length}`}
                  </Button>
                </div>
                <p className="text-xs text-orange-400/70">
                  Example: If you sold tickets 1-38, enter 39 to exclude 39-{paperTickets.length}
                </p>
              </div>

              {/* Summary */}
              <div className="flex items-center justify-between py-2 border-y border-slate-700">
                <span className="text-sm">
                  <strong className="text-emerald-400">{paperTickets.length - excludedTickets.size}</strong> in play
                  {excludedTickets.size > 0 && (
                    <span className="text-orange-400 ml-2">
                      (<strong>{excludedTickets.size}</strong> excluded)
                    </span>
                  )}
                </span>
                {excludedTickets.size > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearExclusions} className="text-red-400 hover:text-red-300 hover:bg-red-900/20">
                    <X className="w-4 h-4 mr-1" /> Clear All
                  </Button>
                )}
              </div>

              {/* Ticket grid for manual selection */}
              <div>
                <label className="block text-sm font-medium mb-2 text-slate-300">Or click to toggle individual tickets:</label>
                <div className="max-h-48 overflow-y-auto border border-slate-700 rounded-lg p-2 bg-slate-800/50">
                  <div className="grid grid-cols-10 gap-1">
                    {paperTickets.map((t) => {
                      const num = t.ticket_number;
                      const isExcluded = excludedTickets.has(num);
                      return (
                        <button
                          key={num}
                          onClick={() => toggleExcludeTicket(num)}
                          className={`w-8 h-8 text-xs font-mono rounded transition-colors ${
                            isExcluded 
                              ? 'bg-orange-600 text-white' 
                              : 'bg-slate-700 border border-slate-600 text-slate-300 hover:border-orange-500'
                          }`}
                          title={isExcluded ? `Ticket #${num} excluded` : `Click to exclude #${num}`}
                        >
                          {num}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Orange = excluded (won't count as winner)
                </p>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExcludeDialog(false)} className="border-slate-600 text-slate-300">
                Cancel
              </Button>
              <Button onClick={handleSaveExcludedTickets} className="bg-orange-600 hover:bg-orange-700">
                Save Exclusions
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
